import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { StoreStatusProvider } from './context/StoreStatusContext';
import { SubscriptionCartProvider } from './context/SubscriptionCartContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import SessionExpiredModal from './components/SessionExpiredModal/SessionExpiredModal';
import { startSessionExpiryWatcher } from './utils/sessionExpiry';
import Home from './pages/Home/Home';
import About from './pages/About/About';
import OurMenu from './pages/OurMenu/OurMenu';
import Services from './pages/Services/Services';
import WhyChooseUs from './pages/WhyChooseUs/WhyChooseUs';
import Contact from './pages/Contact/Contact';
import RefundPolicy from './pages/Policy/RefundPolicy';
import PrivacyPolicy from './pages/Policy/PrivacyPolicy';
import TermsConditions from './pages/Policy/TermsConditions';
import FAQ from './pages/Policy/FAQ';
import MealPlans from './pages/MealPlans/MealPlans';
import SubscriptionBuilderPreview from './pages/MealPlans/SubscriptionBuilderPreview';
import Cart from './pages/Cart/Cart';
import SubscriptionCart from './pages/Cart/SubscriptionCart';
import Checkout from './pages/Checkout/Checkout';
import PaymentRedirect from './pages/Checkout/PaymentRedirect';
import Payment from './pages/Account/Payment/Payment';
import PaymentSuccess from './pages/Account/Payment/PaymentSuccess';
import PaymentFailed from './pages/Account/Payment/PaymentFailed';
import PaymentReturn from './pages/Account/Payment/PaymentReturn';
import Gallery from './pages/Gallery/Gallery';
import Reviews from './pages/Reviews/Reviews';
import BulkOrder from './pages/BulkOrder/BulkOrder';
import AccountLayout from './pages/Account/AccountLayout';
import AccountDashboard from './pages/Account/AccountDashboard';
import AccountPlaceholder from './pages/Account/AccountPlaceholder';
import AccountMyOrders from './pages/Account/MyOrders/AccountMyOrders';
import AccountDelete from './pages/Account/AccountDelete';
import AccountUpdateDetails from './pages/Account/AccountUpdateDetails';
import AccountForgotPassword from './pages/Account/AccountForgotPassword';
import AccountResetRedirect from './pages/Account/AccountResetRedirect';
import AccountChangePassword from './pages/Account/AccountChangePassword';
import AccountPasswordUpdated from './pages/Account/AccountPasswordUpdated';
import AccountAddresses from './pages/Account/Addresses/AccountAddresses';
import AccountSubscriptions from './pages/Account/AccountSubscriptions';
import AccountBulkOrders from './pages/Account/AccountBulkOrders';
import { FaArrowUp, FaWhatsapp } from 'react-icons/fa';
import { buildWhatsAppUrl } from './config/whatsapp';
import './App.css';

const WHATSAPP_MEAL_PLAN_MESSAGE = "Hi, I'd like to know more about meal plan";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const [showScroll, setShowScroll] = useState(false);
  const [showWhatsappTooltip, setShowWhatsappTooltip] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScroll(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const openTimer = window.setTimeout(() => {
      setShowWhatsappTooltip(true);
    }, 1000);

    const closeTimer = window.setTimeout(() => {
      setShowWhatsappTooltip(false);
    }, 4500);

    return () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };
  }, []);

  useEffect(() => startSessionExpiryWatcher(), []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <ThemeProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <StoreStatusProvider>
        <CartProvider>
          <SubscriptionCartProvider>
          <ScrollToTop />
          <div className="app">
            <Navbar />
            <SessionExpiredModal />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about-us" element={<About />} />
                <Route path="/our-menu" element={<OurMenu />} />
                <Route path="/services" element={<Services />} />
                <Route path="/whychooseus" element={<WhyChooseUs />} />
                <Route path="/meal-plans" element={<MealPlans />} />
                <Route path="/add-subscription" element={<MealPlans builderOnly />} />
                <Route path="/subscription-builder-preview" element={<SubscriptionBuilderPreview />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/reviews" element={<Reviews />} />
                <Route path="/bulk-order" element={<BulkOrder />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/subscription-cart" element={<SubscriptionCart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/payment" element={<Payment />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/return" element={<PaymentReturn />} />
                <Route path="/payment/failed" element={<PaymentFailed />} />
                <Route path="/payment-redirect" element={<PaymentRedirect />} />
                <Route path="/refund_returns" element={<RefundPolicy />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-conditions" element={<TermsConditions />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/account" element={<AccountLayout />}>
                  <Route index element={<AccountDashboard />} />
                  <Route path="orders" element={<AccountMyOrders />} />
                  <Route path="details" element={<AccountUpdateDetails />} />
                  <Route path="addresses" element={<AccountAddresses />} />
                  <Route path="subscriptions" element={<AccountSubscriptions />} />
                  <Route path="bulk-orders" element={<AccountBulkOrders />} />
                  <Route path="forgot-password" element={<AccountForgotPassword />} />
                  <Route path="reset-password" element={<AccountResetRedirect />} />
                  <Route path="password-updated" element={<AccountPasswordUpdated />} />
                  <Route path="change-password" element={<AccountChangePassword />} />
                  <Route path="delete" element={<AccountDelete />} />
                </Route>
              </Routes>
            </main>
            <Footer />
            <a
              className={`whatsapp-btn${showWhatsappTooltip ? ' whatsapp-btn--show-tooltip' : ''}`}
              href={buildWhatsAppUrl(WHATSAPP_MEAL_PLAN_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat on WhatsApp"
            >
              <FaWhatsapp />
              <span className="whatsapp-btn__tooltip">Hi, need help?</span>
            </a>
            {showScroll && (
              <button className="scroll-top-btn" onClick={scrollToTop} aria-label="Scroll to top">
                <FaArrowUp />
              </button>
            )}
          </div>
          </SubscriptionCartProvider>
        </CartProvider>
        </StoreStatusProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
